import type { UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import {
  CACHE_KEY_HEALTH_PROFILE_IS_UPDATE_DUE,
  CACHE_KEY_HEALTH_PROFILE_MEDICATIONS_TO_VERIFY,
  CACHE_KEY_PRESCRIPTION_TRANSFER_CANDIDATES,
  CACHE_KEY_PRESCRIPTION_TRANSFER_CANDIDATES_WITH_CAPSULE_MEDS,
} from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import type { MedicationSearchResult } from 'src/app/types';

export interface MedicationTransferCandidateResult extends MedicationSearchResult {
  pharmacyName: string;
  pharmacyStreets: string;
  pharmacyPhone: string;
  strength?: string;
  prescriberFirstName?: string;
  prescriberLastName?: string;
  prescriberStreet?: string;
  prescriberStreet2?: string;
  prescriberCity?: string;
  prescriberState?: string;
  prescriberZipCode?: string;
  prescriberPhone?: string;
  prescriberFax?: string;
  dosageFormLabel?: string;
  lastFillDate: string;
  isCapsuleMed: boolean;
}

/** @deprecated use the newer hook: useHealthProfileMedicationsToVerify */
export const useHealthProfileMedications = (
  patientId: string,
  enabled?: boolean
): UseQueryResult<MedicationTransferCandidateResult[], AxiosError> => {
  const authClient = useBffClient();
  return useQuery(
    [CACHE_KEY_PRESCRIPTION_TRANSFER_CANDIDATES, patientId],
    () => {
      return authClient
        .get(`transfers/prescription_candidates/${patientId}`)
        .then((resp: { data: MedicationTransferCandidateResult[] }) =>
          resp.data.filter(med => med.primaryName && med.pharmacyName)
        );
    },
    {
      ...(enabled !== undefined && { enabled }),
      // Cache for 15 minutes, should be enough for the user to go through the form
      staleTime: 1000 * 60 * 15,
    }
  );
};

/** @deprecated use the newer hook: useHealthProfileMedicationsToVerify */
export const useHealthProfileMedicationsWithCapsuleMeds = (
  patientId: string,
  enabled?: boolean
): UseQueryResult<MedicationTransferCandidateResult[], AxiosError> => {
  const authClient = useBffClient();
  return useQuery(
    [CACHE_KEY_PRESCRIPTION_TRANSFER_CANDIDATES_WITH_CAPSULE_MEDS, patientId],
    () => {
      return authClient
        .get(`transfers/prescription_candidates_with_prescriptions/${patientId}`)
        .then((resp: { data: MedicationTransferCandidateResult[] }) => resp.data.filter(med => med.primaryName));
    },
    {
      ...(enabled !== undefined && { enabled }),
      // Cache for 15 minutes, should be enough for the user to go through the form
      staleTime: 1000 * 60 * 15,
    }
  );
};

interface HealthProfileMedicationToVerifyPharmacy {
  name: string;
  street?: string;
  phone?: string;
}

interface HealthProfileMedicationToVerifyEligibility {
  doctorRequiredForTransfer: boolean;
  eligibleForTransfer: boolean;
}

interface HealthProfileMedicationToVerifyPrescriber {
  firstName?: string;
  lastName?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  fax?: string;
}

// Minimum required for presentation in the UI
export interface BaseHealthProfileMedicationToVerify {
  primaryName: string;
  isCapsuleMed: boolean;
  /*
   * strength, gcn dosageFormLabel may not be available for external medications.
   * For those, we fall back upstream in PHK to RX Live drug description, which
   * includes strength and dosage form along with the primaryName.
   */
  gcn?: string;
  strength?: string;
  dosageFormLabel?: string;
}

/*
 * External (RXLive) data required for medication transfer
 */
export type ExternalHealthProfileMedicationToVerify = BaseHealthProfileMedicationToVerify & {
  externalId: string;
  pharmacy: HealthProfileMedicationToVerifyPharmacy;
  eligibility: HealthProfileMedicationToVerifyEligibility;
  prescriber: HealthProfileMedicationToVerifyPrescriber;
  lastFillDate: string;
  isCapsuleMed: false;
};

export type InternalHealthProfileMedicationToVerify = BaseHealthProfileMedicationToVerify & {
  id: number;
  lastFillDate: string;
  isCapsuleMed: true;
};

export type SearchHealthProfileMedicationToVerify = BaseHealthProfileMedicationToVerify & {
  pharmacy: HealthProfileMedicationToVerifyPharmacy;
  eligibility: HealthProfileMedicationToVerifyEligibility;
  isCapsuleMed: false;
  hiclSeqno: string;
};

export type HealthProfileMedicationToVerifyResult =
  | ExternalHealthProfileMedicationToVerify
  | InternalHealthProfileMedicationToVerify;

export type HealthProfileMedicationTransferrable =
  | ExternalHealthProfileMedicationToVerify
  | SearchHealthProfileMedicationToVerify;

export type HealthProfileMedicationToVerifyWithSearch =
  | HealthProfileMedicationToVerifyResult
  | SearchHealthProfileMedicationToVerify;

export type HealthProfileMedicationToVerifyGroup = {
  headerName: string | null;
  medications: HealthProfileMedicationToVerifyResult[];
};

export const useHealthProfileMedicationsToVerify = (
  patientId: string
): UseQueryResult<HealthProfileMedicationToVerifyGroup[], AxiosError> => {
  const authClient = useBffClient();
  return useQuery(
    [CACHE_KEY_HEALTH_PROFILE_MEDICATIONS_TO_VERIFY, patientId],
    () => {
      return authClient
        .get(`health_profile/medications_to_verify/${patientId}`)
        .then((resp: { data: HealthProfileMedicationToVerifyGroup[] }) => resp.data);
    },
    {
      ...(patientId !== undefined && { enabled: !!patientId }),
      // Cache for 15 minutes, should be enough for the user to go through the form
      staleTime: 1000 * 60 * 15,
    }
  );
};

export interface HealthProfileMedicationCreatedResponse {
  id: string;
  version: number;
}

export interface ExternalHealthProfileMedication {
  externalPatientMedicationId: string;
  gcn?: string;
}

export interface InternalHealthProfileMedication {
  patientMedicationId: number;
  gcn?: string;
}

export type HealthProfileMedicationToCreate = ExternalHealthProfileMedication | InternalHealthProfileMedication;

export interface HealthProfileMedicationCreateData {
  patientId: string;
  medications: HealthProfileMedicationToCreate[];
}

export const useUpdateHealthProfileMedications = () => {
  const authClient = useBffClient();

  return useMutation<HealthProfileMedicationCreatedResponse[], AxiosError, Partial<HealthProfileMedicationCreateData>>(
    ({ patientId, medications }) =>
      authClient
        .post(
          `health_profile/bulk_medications`,
          {
            patientId,
            medications,
          },

          { headers: { 'Content-Type': 'application/json' } }
        )
        .then((resp: { data: HealthProfileMedicationCreatedResponse[] }) => resp.data)
  );
};

interface HealthProfileIsUpdateDueResponse {
  isDue: boolean;
}

export const useIsHealthProfileUpdateDue = (patientId?: string): UseQueryResult<boolean, AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_HEALTH_PROFILE_IS_UPDATE_DUE, patientId],
    async () => {
      return authClient
        .post(`health_profile/is_update_due`, { patientId }, { headers: { 'Content-Type': 'application/json' } })
        .then((resp: { data: HealthProfileIsUpdateDueResponse }) => {
          return resp.data.isDue;
        });
    },
    {
      enabled: !!patientId,
      // Cache for 15 minutes, should be enough for the user to go through the form
      staleTime: 1000 * 60 * 15,
    }
  );
};
